/**
 * Source client registry (contracts/wiring.ts `SourceRegistry`).
 *
 * `sourceRegistry` holds every kind: the pull clients of WP6 (rss, youtube, github, mastodon, bluesky,
 * reddit, discourse), telegram (WP9), and email + watcher (WP7, in their own modules). Tests build their
 * own registry with `createSourceRegistry([fakes])` and pass it to createEngine (testkit
 * `makeEngine({ sourceClients })`).
 */

import type { AnySourceClient, SourceClient, SourceKind } from '../contracts/sources.js';
import type { SourceRegistry } from '../contracts/wiring.js';
import { DigesterError } from '../kernel/errors.js';
import { blueskyClient } from './bluesky.js';
import { discourseClient } from './discourse.js';
import { emailClient } from './email.js';
import { githubClient } from './github.js';
import { mastodonClient } from './mastodon.js';
import { redditClient } from './reddit.js';
import { rssClient } from './rss.js';
import { telegramClient } from './telegram.js';
import { watcherClient } from './watcher.js';
import { youtubeClient } from './youtube.js';

export function createSourceRegistry(clients: readonly AnySourceClient[] = []): SourceRegistry {
  const map = new Map<SourceKind, AnySourceClient>();
  for (const c of clients) map.set(c.kind, c);
  return {
    get<K extends SourceKind>(kind: K): SourceClient<K> {
      const client = map.get(kind);
      if (!client) throw new DigesterError('unsupported', `No source client for ${kind} on this runtime`);
      return client as unknown as SourceClient<K>;
    },
    kinds() {
      return [...map.keys()];
    },
    register(client) {
      map.set(client.kind, client);
    },
  };
}

/** Every kind, in the order of contracts/sources.ts `SourceKind`. */
export const DEFAULT_CLIENTS: readonly AnySourceClient[] = [
  rssClient,
  emailClient,
  youtubeClient,
  githubClient,
  telegramClient,
  mastodonClient,
  blueskyClient,
  redditClient,
  discourseClient,
  watcherClient,
];

export const sourceRegistry: SourceRegistry = createSourceRegistry(DEFAULT_CLIENTS);
